import { useEffect, useState } from 'react';
import { matchesProjectFilter, isActiveProject } from '../lib/projectStatus';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Plus, Calendar, IndianRupee, Trash2, Pencil, User, Building, Home, Users, Filter, X, UserMinus, FileText, MapPin, Hash, ToggleLeft, ToggleRight, LayoutGrid, List, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import { formatDate } from '@/utils/dateFormat';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import SearchableCombobox from '@/components/SearchableCombobox';

export default function Projects() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [clients, setClients] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [createClientDialogOpen, setCreateClientDialogOpen] = useState(false);
  const [creatingClient, setCreatingClient] = useState(false);
  const [creatingProject, setCreatingProject] = useState(false);
  const [newClientData, setNewClientData] = useState({
    name: '',
    email: '',
    phone: '',
    client_type: 'residential', // residential or commercial
  });
  
  // Vendor assignment from All Projects view
  const [vendorAssignDialogOpen, setVendorAssignDialogOpen] = useState(false);
  const [assigningProject, setAssigningProject] = useState(null);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [unassigning, setUnassigning] = useState(null); // vendor ID being unassigned
  const [assignedVendorDetails, setAssignedVendorDetails] = useState([]); // Full vendor details for assigned vendors
  
  // View mode and filter states
  const [viewMode, setViewMode] = useState('list'); // 'card' or 'list'
  const [activeFilter, setActiveFilter] = useState('active'); // 'all', 'active', 'inactive'
  
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  const [formData, setFormData] = useState({
    name: '',
    client_id: '',
    budget: '',
    start_date: '',
    end_date: '',
    status: 'pending',
    description: '',
    location: '',
    project_number: '',
    project_type: 'Commercial',
    pricing_model: 'client_to_vendor',
    markup_percentage: '20',
    vendor_markdown_percentage: '20',
    admin_markup_percentage: '20',
    client_markup_percentage: '20',
  });

  const [editFormData, setEditFormData] = useState({
    name: '',
    client_id: '',
    budget: '',
    start_date: '',
    end_date: '',
    status: 'pending',
    description: '',
    location: '',
    project_number: '',
    project_type: 'Commercial',
    pricing_model: 'vendor_to_client',
    markup_percentage: '20',
    vendor_markdown_percentage: '20',
    admin_markup_percentage: '20',
    client_markup_percentage: '20',
    external_consultant_id: '',
    external_consultant_name: '',
    external_consultant_email: '',
    project_manager_id: '',
  });

  useEffect(() => {
    fetchProjects();
    fetchClients();
    if (user.role === 'owner' || user.role === 'staff') {
      fetchUsers();
      fetchVendors();
    }
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      const clientUsers = response.data.filter((u) => u.role === 'client');
      setUsers(clientUsers);
      const consultantUsers = response.data.filter((u) => ['consultant', 'architect', 'external_designer', 'interior_designer'].includes(u.role));
      setConsultants(consultantUsers);
      const team = response.data.filter((u) => ['owner', 'admin', 'staff', 'project_manager', 'designer', 'site_engineer'].includes(u.role));
      setTeamMembers(team);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      const clientUsers = response.data.filter((u) => u.role === 'client');
      setClients(clientUsers);
    } catch (error) {
      console.error('Failed to load clients');
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      const vendorUsers = response.data.filter((u) => u.role === 'vendor');
      setVendors(vendorUsers);
    } catch (error) {
      console.error('Failed to load vendors');
    }
  };

  // Open vendor assignment dialog for a project
  const openVendorAssignDialog = (project, e) => {
    e.stopPropagation();
    setAssigningProject(project);
    setSelectedVendors(project.assigned_vendors || []);
    // Get full details of assigned vendors
    const assignedDetails = vendors.filter(v => (project.assigned_vendors || []).includes(v.id));
    setAssignedVendorDetails(assignedDetails);
    setVendorAssignDialogOpen(true);
  };

  // Unassign a vendor from project
  const handleUnassignVendor = async (vendorId) => {
    if (!assigningProject) return;
    
    setUnassigning(vendorId);
    try {
      await axios.delete(`${API}/projects/${assigningProject.id}/vendors/${vendorId}`);
      
      // Update local state
      const updatedVendors = selectedVendors.filter(id => id !== vendorId);
      setSelectedVendors(updatedVendors);
      setAssignedVendorDetails(assignedVendorDetails.filter(v => v.id !== vendorId));
      
      toast.success('Vendor removed from project');
      fetchProjects(); // Refresh projects list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove vendor');
    } finally {
      setUnassigning(null);
    }
  };

  // Assign selected vendors to project
  const handleAssignVendors = async () => {
    if (!assigningProject) return;
    
    setAssigning(true);
    try {
      await axios.post(`${API}/projects/${assigningProject.id}/vendors/assign`, selectedVendors);
      toast.success(`Vendors assigned to ${assigningProject.name}. They have been notified.`);
      setVendorAssignDialogOpen(false);
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign vendors');
    } finally {
      setAssigning(false);
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!newClientData.name || newClientData.name.trim().length < 2) {
      toast.error('Client name is required (minimum 2 characters)');
      return;
    }
    if (!newClientData.email && !newClientData.phone) {
      toast.error('At least email or phone is required');
      return;
    }
    if (newClientData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newClientData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setCreatingClient(true);
    try {
      // Use admin endpoint to create client (bypasses email verification)
      const response = await axios.post(`${API}/admin/create-client`, {
        name: newClientData.name.trim(),
        // iter-381: `.local` TLDs fail EmailStr validation on the backend;
        // use a synthetic-but-valid domain when the user leaves email blank.
        email: newClientData.email || `noreply+${Date.now()}@bellacasa.example`,
        phone: newClientData.phone,
        password: 'Client123!', // Meets password requirements
        role: 'client',
        client_type: newClientData.client_type,
      });
      
      // FIX: Backend returns user data directly (not wrapped in 'user' object)
      if (response.data && response.data.id) {
        toast.success(`Client "${newClientData.name}" created successfully`);
        setCreateClientDialogOpen(false);
        await fetchClients();
        // Auto-select the new client
        setFormData({ ...formData, client_id: response.data.id });
        setNewClientData({ name: '', email: '', phone: '', client_type: 'residential' });
      } else {
        throw new Error('Client creation failed - no user returned');
      }
    } catch (error) {
      console.error('Client creation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create client. Please try again.');
    } finally {
      setCreatingClient(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || formData.name.trim().length < 2) {
      toast.error('Project name is required (minimum 2 characters)');
      return;
    }
    if (!formData.client_id) {
      toast.error('Please select or create a client');
      return;
    }
    if (!formData.budget || parseFloat(formData.budget) <= 0) {
      toast.error('Please enter a valid budget');
      return;
    }
    if (formData.start_date && formData.end_date && new Date(formData.end_date) <= new Date(formData.start_date)) {
      toast.error('End date must be after start date');
      return;
    }
    
    setCreatingProject(true);
    try {
      const response = await axios.post(`${API}/projects`, {
        ...formData,
        name: formData.name.trim(),
        budget: parseFloat(formData.budget),
        location: formData.location?.trim() || null,
        project_number: formData.project_number?.trim() || null,
        pricing_model: formData.pricing_model || 'client_to_vendor',
        markup_percentage: parseFloat(formData.client_markup_percentage || '20'),
        vendor_markdown_percentage: parseFloat(formData.admin_markup_percentage || '20'),
        admin_markup_percentage: parseFloat(formData.admin_markup_percentage || '20'),
        client_markup_percentage: parseFloat(formData.client_markup_percentage || '20'),
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
      });
      
      toast.success('Project created successfully!');
      setDialogOpen(false);
      
      // Navigate to the new project if ID is returned
      if (response.data?.id) {
        navigate(`/projects/${response.data.id}`);
      } else {
        fetchProjects();
      }
      
      // Reset form
      setFormData({
        name: '',
        client_id: '',
        budget: '',
        start_date: '',
        end_date: '',
        status: 'pending',
        description: '',
        location: '',
        project_number: '',
        markup_percentage: '20',
        vendor_markdown_percentage: '20',
        admin_markup_percentage: '20',
        client_markup_percentage: '20',
      });
    } catch (error) {
      console.error('Project creation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create project. Please try again.');
    } finally {
      setCreatingProject(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedProject) return;
    try {
      await axios.delete(`${API}/projects/${selectedProject.id}`);
      toast.success('Project deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedProject(null);
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete project');
    }
  };

  // iter-386u-ar — Bulk selection + delete.
  const [selectedProjectIds, setSelectedProjectIds] = useState(new Set());
  const toggleProjectSelected = (id) => {
    setSelectedProjectIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearProjectSelection = () => setSelectedProjectIds(new Set());
  const bulkDeleteProjects = async () => {
    const ids = [...selectedProjectIds];
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} project${ids.length !== 1 ? 's' : ''} and every associated task, BOQ, invoice, document and PO?\n\nThis cannot be undone.`)) return;
    try {
      const r = await axios.post(`${API}/projects/bulk-delete`, { project_ids: ids });
      toast.success(`Deleted ${r.data?.deleted || 0} project${(r.data?.deleted || 0) !== 1 ? 's' : ''}`);
      clearProjectSelection();
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk delete failed');
    }
  };

  const handleRemoveClient = async (projectId) => {
    if (!window.confirm('Are you sure you want to remove the client from this project? The client will no longer have access to project documents.')) return;
    
    try {
      await axios.delete(`${API}/projects/${projectId}/client`);
      toast.success('Client removed from project');
      fetchProjects();
      // Update edit form if currently editing this project
      if (selectedProject?.id === projectId) {
        setEditFormData(prev => ({ ...prev, client_id: '' }));
        setSelectedProject(prev => prev ? { ...prev, client_id: null, client_name: null } : null);
      }
      setEditDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove client');
    }
  };

  // P2 NEW: Quick BOQ - Create BOQ from all approved tasks
  const handleQuickBOQ = async (project, e) => {
    e.stopPropagation();
    try {
      const response = await axios.post(`${API}/boq-worksheets/from-project/${project.id}`);
      toast.success(`BOQ ${response.data.worksheet_number} created with ${response.data.item_count || 0} items!`);
      navigate('/boq-worksheets');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create BOQ');
    }
  };

  const openEditDialog = (project, e) => {
    e.stopPropagation();
    setSelectedProject(project);
    setEditFormData({
      name: project.name,
      client_id: project.client_id || '',
      budget: (project.budget || 0).toString(),
      start_date: project.start_date ? project.start_date.split('T')[0] : '',
      end_date: project.end_date ? project.end_date.split('T')[0] : '',
      status: project.status,
      description: project.description || '',
      location: project.location || '',
      project_number: project.project_number || '',
      project_type: project.project_type || 'Commercial',
      pricing_model: project.pricing_model || 'vendor_to_client',
      markup_percentage: project.markup_percentage?.toString() || '20',
      vendor_markdown_percentage: project.vendor_markdown_percentage?.toString() || '20',
      admin_markup_percentage: (project.admin_markup_percentage || project.vendor_markdown_percentage || 20).toString(),
      client_markup_percentage: (project.client_markup_percentage || project.markup_percentage || 20).toString(),
      external_consultant_id: project.external_consultant_id || '',
      external_consultant_name: project.external_consultant_name || '',
      external_consultant_email: project.external_consultant_email || '',
      project_manager_id: project.project_manager_id || '',
    });
    setEditDialogOpen(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedProject) return;
    try {
      await axios.put(`${API}/projects/${selectedProject.id}`, {
        name: editFormData.name,
        client_id: editFormData.client_id || null,
        budget: parseFloat(editFormData.budget),
        start_date: editFormData.start_date ? new Date(editFormData.start_date).toISOString() : null,
        end_date: editFormData.end_date ? new Date(editFormData.end_date).toISOString() : null,
        status: editFormData.status,
        description: editFormData.description,
        location: editFormData.location?.trim() || null,
        project_number: editFormData.project_number?.trim() || null,
        project_type: editFormData.project_type,
        pricing_model: editFormData.pricing_model || 'vendor_to_client',
        markup_percentage: parseFloat(editFormData.client_markup_percentage),
        vendor_markdown_percentage: parseFloat(editFormData.admin_markup_percentage),
        admin_markup_percentage: parseFloat(editFormData.admin_markup_percentage),
        client_markup_percentage: parseFloat(editFormData.client_markup_percentage),
        external_consultant_id: editFormData.external_consultant_id || null,
        external_consultant_name: editFormData.external_consultant_name || null,
        external_consultant_email: editFormData.external_consultant_email || null,
        project_manager_id: editFormData.project_manager_id || null,
      });
      toast.success('Project updated successfully');
      setEditDialogOpen(false);
      setSelectedProject(null);
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update project');
    }
  };

  const getStatusClass = (status) => {
    const classes = {
      pending: 'status-pending',
      in_progress: 'status-in-progress',
      completed: 'status-completed',
      confirmed: 'status-confirmed',
      delayed: 'status-delayed',
      completed_with_snags: 'bg-sky-100 text-sky-700',
      completed_with_extra_works: 'bg-orange-100 text-orange-700',
      fully_completed: 'bg-emerald-100 text-emerald-700',
      token_pending: 'bg-amber-100 text-amber-700',
      rejected: 'bg-red-50 text-red-600 border border-red-200',
    };
    return classes[status] || 'status-pending';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pending',
      in_progress: 'In Progress',
      completed: 'Completed',
      confirmed: 'Confirmed',
      delayed: 'Delayed',
      completed_with_snags: 'Completed - Snags Pending',
      completed_with_extra_works: 'Completed - Extra Works',
      fully_completed: '100% Completed',
      token_pending: 'Token Pending',
      rejected: 'Rejected',
    };
    return labels[status] || status?.replace(/_/g, ' ') || status;
  };

  // Toggle project Active/Inactive status
  const handleToggleProjectActive = async (project, e) => {
    e.stopPropagation();
    const newStatus = project.is_active === false ? true : false;
    try {
      await axios.patch(`${API}/projects/${project.id}`, {
        is_active: newStatus
      });
      toast.success(`Project marked as ${newStatus ? 'Active' : 'Inactive'}`);
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update project status');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12" data-testid="loading-state">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="projects-page">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="font-playfair text-4xl font-bold text-stone-900 mb-2" data-testid="projects-title">Projects</h1>
            <p className="text-stone-600">Manage all your interior design projects</p>
          </div>
          <div className="flex items-center gap-4">
            {/* View Toggle */}
            <div className="flex items-center bg-stone-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('card')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'card' 
                    ? 'bg-white shadow-sm text-stone-900' 
                    : 'text-stone-500 hover:text-stone-700'
                }`}
                title="Card View"
              >
                <LayoutGrid size={18} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'list' 
                    ? 'bg-white shadow-sm text-stone-900' 
                    : 'text-stone-500 hover:text-stone-700'
                }`}
                title="List View"
              >
                <List size={18} />
              </button>
            </div>
            
            {(user.role === 'owner' || user.role === 'staff' || user.role === 'admin') && (
            <>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary flex items-center gap-2" data-testid="create-project-button">
                  <Plus size={20} />
                  New Project
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl" data-testid="create-project-dialog">
                <DialogHeader>
                  <DialogTitle className="font-playfair text-2xl">Create New Project</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label>Project Name</Label>
                    <Input
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      data-testid="project-name-input"
                    />
                  </div>

                  <div>
                    <Label>Client</Label>
                    <SearchableCombobox
                      options={clients.map(c => ({ 
                        value: c.id, 
                        label: `${c.name} (${c.client_type === 'commercial' ? 'Commercial' : 'Residential'}) - ${c.email}` 
                      }))}
                      value={formData.client_id}
                      onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                      placeholder="Search or select client..."
                      searchPlaceholder="Type to search clients..."
                      allowCreate={user.role === 'owner' || user.role === 'staff'}
                      onCreateNew={() => setCreateClientDialogOpen(true)}
                      createNewText="+ Add New Client"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Budget (₹)</Label>
                      <Input
                        type="number"
                        required
                        value={formData.budget}
                        onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                        data-testid="budget-input"
                      />
                    </div>
                    <div>
                      <Label>Project Type</Label>
                      <Select
                        value={formData.project_type}
                        onValueChange={(value) => setFormData({ ...formData, project_type: value })}
                      >
                        <SelectTrigger data-testid="project-type-select">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Residential">Residential</SelectItem>
                          <SelectItem value="Commercial">Commercial</SelectItem>
                          <SelectItem value="Industrial">Industrial</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Status</Label>
                      <Select
                        value={formData.status}
                        onValueChange={(value) => setFormData({ ...formData, status: value })}
                      >
                        <SelectTrigger data-testid="status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="delayed">Delayed</SelectItem>
                      <SelectItem value="rejected" data-testid="status-option-rejected">Rejected</SelectItem>
                          <SelectItem value="completed_with_snags">Completed - Snags Pending</SelectItem>
                          <SelectItem value="completed_with_extra_works">Completed - Extra Works Pending</SelectItem>
                          <SelectItem value="fully_completed">100% Completed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Start Date <span className="text-stone-400 font-normal">(optional)</span></Label>
                      <Input
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                        data-testid="start-date-input"
                      />
                    </div>
                    <div>
                      <Label>End Date <span className="text-stone-400 font-normal">(optional)</span></Label>
                      <Input
                        type="date"
                        value={formData.end_date}
                        onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                        data-testid="end-date-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Description</Label>
                    <Textarea
                      rows={3}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      data-testid="description-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Location</Label>
                      <Input
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        placeholder="e.g., Mumbai, Maharashtra"
                        data-testid="location-input"
                      />
                      <p className="text-xs text-stone-500 mt-1">
                        Project site location
                      </p>
                    </div>
                    <div>
                      <Label>Project Number</Label>
                      <Input
                        value={formData.project_number}
                        onChange={(e) => setFormData({ ...formData, project_number: e.target.value })}
                        placeholder="Auto-generated if left empty"
                        data-testid="project-number-input"
                      />
                      <p className="text-xs text-stone-500 mt-1">
                        Unique project identifier (auto-generated)
                      </p>
                    </div>
                  </div>

                  {/* Pricing Model Toggle */}
                  <div className="bg-stone-50 rounded-lg p-4 border border-stone-200">
                    <Label className="text-sm font-semibold text-stone-800 block mb-3">Pricing Model</Label>
                    <div className="flex gap-2">
                      <button type="button"
                        onClick={() => setFormData({ ...formData, pricing_model: 'vendor_to_client' })}
                        className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium border transition-all ${
                          formData.pricing_model !== 'client_to_vendor'
                            ? 'bg-stone-900 text-white border-stone-900'
                            : 'bg-white text-stone-600 border-stone-300 hover:bg-stone-100'
                        }`}
                        data-testid="pricing-model-vtc"
                      >
                        Vendor → Client
                        <span className="block text-[10px] font-normal opacity-80 mt-0.5">Input vendor rate, markup to client</span>
                      </button>
                      <button type="button"
                        onClick={() => setFormData({ ...formData, pricing_model: 'client_to_vendor' })}
                        className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium border transition-all ${
                          formData.pricing_model === 'client_to_vendor'
                            ? 'bg-stone-900 text-white border-stone-900'
                            : 'bg-white text-stone-600 border-stone-300 hover:bg-stone-100'
                        }`}
                        data-testid="pricing-model-ctv"
                      >
                        Client → Vendor
                        <span className="block text-[10px] font-normal opacity-80 mt-0.5">Input client rate, markdown to vendor</span>
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{formData.pricing_model === 'client_to_vendor' ? 'Admin Markdown (%)' : 'Admin Markup (%)'}</Label>
                      <Input
                        type="number"
                        step="0.1"
                        required
                        value={formData.admin_markup_percentage}
                        onChange={(e) => setFormData({ ...formData, admin_markup_percentage: e.target.value })}
                        placeholder="20"
                        data-testid="admin-markup-input"
                      />
                      <p className="text-xs text-stone-500 mt-1">
                        {formData.pricing_model === 'client_to_vendor'
                          ? 'Markdown on client rate for admin pricing'
                          : 'Markup on vendor rate for admin pricing'}
                      </p>
                    </div>
                    <div>
                      <Label>{formData.pricing_model === 'client_to_vendor' ? 'Vendor Markdown (%)' : 'Client Markup (%)'}</Label>
                      <Input
                        type="number"
                        step="0.1"
                        required
                        value={formData.client_markup_percentage}
                        onChange={(e) => setFormData({ ...formData, client_markup_percentage: e.target.value })}
                        placeholder="20"
                        data-testid="client-markup-input"
                      />
                      <p className="text-xs text-stone-500 mt-1">
                        {formData.pricing_model === 'client_to_vendor'
                          ? 'Markdown on admin rate for vendor pricing'
                          : 'Markup on admin rate for client pricing'}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3 justify-end">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={creatingProject}>
                      Cancel
                    </Button>
                    <Button 
                      type="submit"
                      className="btn-primary" 
                      data-testid="submit-project-button"
                      disabled={creatingProject || !formData.name || !formData.client_id || !formData.budget}
                    >
                      {creatingProject ? 'Creating...' : 'Create Project'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={createClientDialogOpen} onOpenChange={setCreateClientDialogOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-playfair text-xl flex items-center gap-2">
                    <User size={20} />
                    Add New Client
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateClient} className="space-y-4">
                  <div>
                    <Label>Client Name *</Label>
                    <Input
                      required
                      value={newClientData.name}
                      onChange={(e) => setNewClientData({ ...newClientData, name: e.target.value })}
                      placeholder="e.g., John Smith"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={newClientData.email}
                      onChange={(e) => setNewClientData({ ...newClientData, email: e.target.value })}
                      placeholder="client@example.com (optional)"
                    />
                    <p className="text-[10px] text-stone-400 mt-0.5">Optional — a placeholder email is auto-generated if left blank.</p>
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={newClientData.phone}
                      onChange={(e) => setNewClientData({ ...newClientData, phone: e.target.value })}
                      placeholder="e.g., 9876543210"
                    />
                  </div>
                  <div>
                    <Label>Client Type *</Label>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <button
                        type="button"
                        onClick={() => setNewClientData({ ...newClientData, client_type: 'residential' })}
                        className={`p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${
                          newClientData.client_type === 'residential'
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-stone-200 hover:border-stone-300'
                        }`}
                      >
                        <Home size={24} />
                        <span className="font-medium">Residential</span>
                        <span className="text-xs text-stone-500">Homes & Apartments</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setNewClientData({ ...newClientData, client_type: 'commercial' })}
                        className={`p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${
                          newClientData.client_type === 'commercial'
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-stone-200 hover:border-stone-300'
                        }`}
                      >
                        <Building size={24} />
                        <span className="font-medium">Commercial</span>
                        <span className="text-xs text-stone-500">Offices & Retail</span>
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-stone-500 bg-stone-50 p-2 rounded">
                    A default password will be set. Client should change it on first login.
                  </p>
                  <div className="flex gap-3 justify-end">
                    <Button type="button" variant="outline" onClick={() => setCreateClientDialogOpen(false)} disabled={creatingClient}>
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      className="btn-primary"
                      disabled={creatingClient || !newClientData.name}
                    >
                      {creatingClient ? (
                        <>Creating...</>
                      ) : (
                        <>
                          <Plus size={16} className="mr-1" />
                          Add Client
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            </>
          )}
          </div>
        </div>

        {/* Main Content with Filter Sidebar */}
        <div className="flex gap-6">
          {/* Filter Sidebar */}
          <div className="w-48 flex-shrink-0">
            <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
              <button
                onClick={() => setActiveFilter('all')}
                className={`w-full px-4 py-3 text-left text-sm font-medium flex items-center justify-between transition-colors ${
                  activeFilter === 'all' 
                    ? 'bg-stone-900 text-white' 
                    : 'text-stone-700 hover:bg-stone-50'
                }`}
              >
                All Projects
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  activeFilter === 'all' ? 'bg-white/20 text-white' : 'bg-stone-100 text-stone-600'
                }`}>
                  {projects.length}
                </span>
              </button>
              <button
                onClick={() => setActiveFilter('active')}
                className={`w-full px-4 py-3 text-left text-sm font-medium flex items-center justify-between border-t border-stone-100 transition-colors ${
                  activeFilter === 'active' 
                    ? 'bg-green-600 text-white' 
                    : 'text-stone-700 hover:bg-stone-50'
                }`}
              >
                Active
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  activeFilter === 'active' ? 'bg-white/20 text-white' : 'bg-green-100 text-green-600'
                }`}>
                  {projects.filter(isActiveProject).length}
                </span>
              </button>
              <button
                onClick={() => setActiveFilter('inactive')}
                className={`w-full px-4 py-3 text-left text-sm font-medium flex items-center justify-between border-t border-stone-100 transition-colors ${
                  activeFilter === 'inactive' 
                    ? 'bg-stone-600 text-white' 
                    : 'text-stone-700 hover:bg-stone-50'
                }`}
              >
                Inactive
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  activeFilter === 'inactive' ? 'bg-white/20 text-white' : 'bg-stone-200 text-stone-600'
                }`}>
                  {projects.filter(p => !isActiveProject(p)).length}
                </span>
              </button>
            </div>
          </div>

          {/* Projects Content */}
          <div className="flex-1">
            {/* Filtered Projects */}
            {(() => {
              const filteredProjects = projects.filter(p => {
                                return matchesProjectFilter(p, activeFilter);
              });
              
              if (filteredProjects.length === 0) {
                return (
                  <div className="text-center py-16 bg-white border border-stone-200 rounded-lg" data-testid="no-projects">
                    <p className="text-stone-500">
                      {activeFilter === 'all' ? 'No projects yet. Create your first project to get started.' : `No ${activeFilter} projects found.`}
                    </p>
                  </div>
                );
              }

              // LIST VIEW
              if (viewMode === 'list') {
                return (
                  <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
                    {/* iter-386u-as — bulk-delete action bar */}
                    {user?.role === 'owner' && selectedProjectIds.size > 0 && (
                      <div className="sticky top-0 z-10 flex items-center justify-between bg-rose-50 border-b border-rose-200 px-4 py-2" data-testid="project-bulk-toolbar">
                        <div className="text-sm text-rose-900 font-medium">
                          {selectedProjectIds.size} project{selectedProjectIds.size !== 1 ? 's' : ''} selected
                        </div>
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="ghost" onClick={clearProjectSelection}>Clear</Button>
                          <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white" onClick={bulkDeleteProjects} data-testid="project-bulk-delete-btn">
                            Delete {selectedProjectIds.size} project{selectedProjectIds.size !== 1 ? 's' : ''}
                          </Button>
                        </div>
                      </div>
                    )}
                    <table className="w-full">
                      <thead className="bg-stone-50 border-b border-stone-200">
                        <tr>
                          {/* iter-386u-as — bulk-select header (owner only) */}
                          {user?.role === 'owner' && (
                            <th className="w-8 px-2 py-3">
                              <input
                                type="checkbox"
                                className="h-4 w-4 accent-rose-600 cursor-pointer"
                                checked={filteredProjects.length > 0 && filteredProjects.every((p) => selectedProjectIds.has(p.id))}
                                onChange={(e) => {
                                  if (e.target.checked) setSelectedProjectIds(new Set(filteredProjects.map((p) => p.id)));
                                  else clearProjectSelection();
                                }}
                                data-testid="project-select-all"
                                aria-label="Select all projects"
                              />
                            </th>
                          )}
                          <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">#</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Project Name</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Client</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Type</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Budget</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Start Date</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">End Date</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Status</th>
                          {/* iter-386u-d — Actions column hidden for vendor/client */}
                          {['owner', 'admin', 'staff'].includes(user.role) && (
                            <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Actions</th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-stone-100">
                        {filteredProjects.map((project, index) => (
                          <tr 
                            key={project.id} 
                            className={`hover:bg-stone-50 cursor-pointer transition-colors group ${selectedProjectIds.has(project.id) ? 'bg-rose-50' : ''}`}
                            onClick={() => navigate(`/projects/${project.id}`)}
                          >
                            {/* iter-386u-as — bulk-select checkbox (owner only, non-navigating) */}
                            {user?.role === 'owner' && (
                              <td className="px-2 py-3" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 accent-rose-600 cursor-pointer"
                                  checked={selectedProjectIds.has(project.id)}
                                  onChange={() => toggleProjectSelected(project.id)}
                                  data-testid={`project-select-${project.id}`}
                                  aria-label={`Select ${project.name}`}
                                />
                              </td>
                            )}
                            <td className="px-4 py-3 text-sm text-stone-500 font-medium">{index + 1}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-stone-900">{project.name}</span>
                                {project.is_active === false && (
                                  <span className="px-1.5 py-0.5 rounded text-xs bg-stone-200 text-stone-600">Inactive</span>
                                )}
                              </div>
                              {project.project_number && (
                                <span className="text-xs text-stone-500">{project.project_number}</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-stone-600" data-testid="project-client-cell">
                              <div>{project.client_name || 'No client'}</div>
                              {project.project_manager_name && (
                                <div className="text-xs text-stone-400 mt-0.5">PM: {project.project_manager_name}</div>
                              )}
                              {project.external_consultant_name && (
                                <div className="text-xs text-stone-400 mt-0.5">Consultant: {project.external_consultant_name}</div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-stone-600">
                              {project.project_type || 'Commercial'}
                            </td>
                            <td className="px-4 py-3 text-sm text-stone-900 font-medium">
                              ₹{(project.budget || 0).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-stone-600" data-testid="project-start-date">
                              {project.start_date ? formatDate(project.start_date) : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-stone-600" data-testid="project-end-date">
                              {project.end_date ? formatDate(project.end_date) : '-'}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`status-badge ${getStatusClass(project.status)}`}>
                                {getStatusLabel(project.status)}
                              </span>
                            </td>
                            {/* iter-386u-d — Actions column hidden for vendor/client */}
                            {['owner', 'admin', 'staff'].includes(user.role) && (
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                {['owner', 'admin'].includes(user.role) && (
                                  <button
                                    onClick={(e) => handleToggleProjectActive(project, e)}
                                    className={`p-1.5 rounded ${
                                      project.is_active === false 
                                        ? 'text-stone-400 hover:text-stone-600 hover:bg-stone-100' 
                                        : 'text-green-500 hover:text-green-700 hover:bg-green-50'
                                    }`}
                                    title={project.is_active === false ? 'Mark as Active' : 'Mark as Inactive'}
                                  >
                                    {project.is_active === false ? <ToggleLeft size={14} /> : <ToggleRight size={14} />}
                                  </button>
                                )}
                                <button
                                  onClick={(e) => handleQuickBOQ(project, e)}
                                  className="p-1.5 text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 rounded"
                                  title="Quick BOQ"
                                >
                                  <FileText size={14} />
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); openVendorAssignDialog(project, e); }}
                                  className="p-1.5 text-purple-500 hover:text-purple-700 hover:bg-purple-50 rounded"
                                  title="Assign Vendors"
                                >
                                  <Users size={14} />
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); openEditDialog(project, e); }}
                                  className="p-1.5 text-stone-500 hover:text-stone-700 hover:bg-stone-100 rounded"
                                  title="Edit Project"
                                >
                                  <Pencil size={14} />
                                </button>
                                {user.role === 'owner' && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setSelectedProject(project); setDeleteDialogOpen(true); }}
                                    className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                                    title="Delete Project"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                )}
                                <button
                                  onClick={(e) => { e.stopPropagation(); navigate(`/projects/${project.id}`); }}
                                  className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded"
                                  title="Open Project"
                                >
                                  <ChevronRight size={14} />
                                </button>
                              </div>
                            </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              }

              // CARD VIEW (existing)
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
                  {filteredProjects.map((project) => (
                    <div
                      key={project.id}
                      className="project-card p-6 cursor-pointer group relative"
                      data-testid={`project-card-${project.id}`}
                      onClick={() => navigate(`/projects/${project.id}`)}
                    >
                      {/* Status Badge - Top Right */}
                      <div className="absolute top-4 right-4 flex gap-2">
                        {project.is_active === false && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-stone-200 text-stone-600">
                            Inactive
                          </span>
                        )}
                        <span className={`status-badge ${getStatusClass(project.status)}`} data-testid="project-status">
                          {getStatusLabel(project.status)}
                        </span>
                      </div>

                      {/* Project Name */}
                      <h3 className="font-playfair text-xl font-bold text-stone-900 pr-28 mb-2" data-testid="project-name">
                        {project.name}
                      </h3>

                      {/* Description */}
                      {project.description && (
                        <p className="text-stone-600 text-sm mb-4 line-clamp-2" data-testid="project-description">
                          {project.description}
                        </p>
                      )}

                      {/* Project Number */}
                      {project.project_number && (
                        <div className="flex items-center gap-2 mb-2">
                          <Hash size={14} className="text-stone-400" />
                          <span className="text-xs text-stone-500 font-mono">{project.project_number}</span>
                        </div>
                      )}

                      {/* Location */}
                      {project.location && (
                        <div className="flex items-center gap-2 mb-2">
                          <MapPin size={14} className="text-emerald-500" />
                          <span className="text-xs text-emerald-600">{project.location}</span>
                        </div>
                      )}

                      {/* Assigned Vendors */}
                      {project.assigned_vendors?.length > 0 && (
                        <div className="flex items-center gap-2 mb-3">
                          <Users size={14} className="text-purple-500" />
                          <span className="text-xs text-purple-600">{project.assigned_vendors.length} vendor(s) assigned</span>
                        </div>
                      )}

                      {/* Client Assignment */}
                      <div className="flex items-center gap-2 mb-1">
                        <User size={14} className="text-blue-500" />
                        <span className="text-xs text-blue-600">
                          {project.client_name || (project.client_id ? 'Client assigned' : 'No client assigned')}
                        </span>
                      </div>
                      {project.external_consultant_name && (
                        <div className="flex items-center gap-2 mb-3">
                          <User size={14} className="text-amber-500" />
                          <span className="text-xs text-amber-600">
                            Consultant: {project.external_consultant_name}
                          </span>
                        </div>
                      )}
                      {!project.external_consultant_name && <div className="mb-2" />}

                      {/* Budget & Dates - Budget hidden from vendors */}
                      <div className="space-y-2 text-sm">
                        {project.budget !== undefined && project.budget !== null && (
                          <div className="flex items-center gap-2 text-stone-600">
                            <IndianRupee size={16} />
                            <span data-testid="project-budget">₹{(project.budget || 0).toLocaleString()}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-stone-600">
                          <Calendar size={16} />
                          <span data-testid="project-dates">
                            {project.start_date ? formatDate(project.start_date) : 'No start date'} -{' '}
                            {project.end_date ? formatDate(project.end_date) : 'No end date'}
                          </span>
                        </div>
                      </div>

                      {/* Action Buttons - Bottom Right on Hover */}
                      {(user.role === 'owner' || user.role === 'staff' || user.role === 'admin') && (
                        <div className="absolute bottom-4 right-4 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10 bg-white/90 rounded-lg p-1 shadow-sm border border-stone-100">
                          {/* Active/Inactive Toggle - Owner/Admin only */}
                          {['owner', 'admin'].includes(user.role) && (
                            <button
                              onClick={(e) => handleToggleProjectActive(project, e)}
                              className={`p-2 rounded-lg ${
                                project.is_active === false 
                                  ? 'text-stone-400 hover:text-stone-600 hover:bg-stone-50' 
                                  : 'text-green-500 hover:text-green-700 hover:bg-green-50'
                              }`}
                              title={project.is_active === false ? 'Mark as Active' : 'Mark as Inactive'}
                              data-testid={`toggle-active-${project.id}`}
                            >
                              {project.is_active === false ? <ToggleLeft size={16} /> : <ToggleRight size={16} />}
                            </button>
                          )}
                          {/* P2 NEW: Quick BOQ Button */}
                          <button
                            onClick={(e) => handleQuickBOQ(project, e)}
                            className="p-2 text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg"
                            title="Quick BOQ - Create BOQ from approved tasks"
                            data-testid={`quick-boq-${project.id}`}
                          >
                            <FileText size={16} />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); openVendorAssignDialog(project, e); }}
                            className="p-2 text-purple-500 hover:text-purple-700 hover:bg-purple-50 rounded-lg"
                            title="Assign Vendors"
                            data-testid={`assign-vendors-${project.id}`}
                          >
                            <Users size={16} />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); openEditDialog(project, e); }}
                            className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg"
                            title="Edit Project"
                          >
                            <Pencil size={16} />
                          </button>
                          {user.role === 'owner' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setSelectedProject(project); setDeleteDialogOpen(true); }}
                              className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg"
                              title="Delete Project"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>

        {/* Delete Project Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Trash2 className="text-red-500" size={20} />
                Delete Project
              </AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <strong>{selectedProject?.name}</strong>? 
                This will also delete all associated tasks, BOQs, invoices, and documents. 
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setSelectedProject(null)}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                Delete Project
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Edit Project Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="edit-project-dialog">
            <DialogHeader className="shrink-0">
              <DialogTitle className="font-playfair text-2xl">Edit Project</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEdit} className="space-y-4 overflow-y-auto flex-1 pr-2">
              <div>
                <Label>Project Name</Label>
                <Input
                  required
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  data-testid="edit-project-name-input"
                />
              </div>

              <div>
                <Label>Client</Label>
                <div className="flex gap-2">
                  <Select
                    value={editFormData.client_id}
                    onValueChange={(value) => setEditFormData({ ...editFormData, client_id: value })}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name} ({u.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {editFormData.client_id && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="shrink-0 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                      onClick={() => handleRemoveClient(selectedProject?.id)}
                      title="Remove client from project"
                      data-testid="remove-client-btn"
                    >
                      <UserMinus size={16} />
                    </Button>
                  )}
                </div>
                {editFormData.client_id && (
                  <p className="text-xs text-stone-500 mt-1">
                    Click the remove button to unassign client from this project
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Budget (₹)</Label>
                  <Input
                    type="number"
                    required
                    value={editFormData.budget}
                    onChange={(e) => setEditFormData({ ...editFormData, budget: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Project Type</Label>
                  <Select
                    value={editFormData.project_type}
                    onValueChange={(value) => setEditFormData({ ...editFormData, project_type: value })}
                  >
                    <SelectTrigger data-testid="edit-project-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Residential">Residential</SelectItem>
                      <SelectItem value="Commercial">Commercial</SelectItem>
                      <SelectItem value="Industrial">Industrial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Status</Label>
                  <Select
                    value={editFormData.status}
                    onValueChange={(value) => setEditFormData({ ...editFormData, status: value })}
                  >
                    <SelectTrigger data-testid="edit-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="delayed">Delayed</SelectItem>
                      <SelectItem value="rejected" data-testid="status-option-rejected">Rejected</SelectItem>
                      <SelectItem value="completed_with_snags">Completed - Snags Pending</SelectItem>
                      <SelectItem value="completed_with_extra_works">Completed - Extra Works Pending</SelectItem>
                      <SelectItem value="fully_completed">100% Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={editFormData.start_date}
                    onChange={(e) => setEditFormData({ ...editFormData, start_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={editFormData.end_date}
                    onChange={(e) => setEditFormData({ ...editFormData, end_date: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  rows={3}
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                />
              </div>

              {/* Project Manager (Bella Casa Team) */}
              <div>
                <Label>Project Manager</Label>
                <Select
                  value={editFormData.project_manager_id || 'none'}
                  onValueChange={(value) => {
                    setEditFormData({ ...editFormData, project_manager_id: value === 'none' ? '' : value });
                  }}
                >
                  <SelectTrigger data-testid="edit-project-manager-select">
                    <SelectValue placeholder="Assign a team member" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not assigned</SelectItem>
                    {teamMembers.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.name || m.email} ({m.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-stone-400 mt-1">Bella Casa team member responsible for this project</p>
              </div>

              <div>
                <Label>External Consultant</Label>
                <div className="flex gap-2">
                  {consultants.length > 0 ? (
                    <Select
                      value={editFormData.external_consultant_id || 'none'}
                      onValueChange={(value) => {
                        if (value === 'none') {
                          setEditFormData({ ...editFormData, external_consultant_id: '', external_consultant_name: '', external_consultant_email: '' });
                        } else {
                          const selected = consultants.find(c => c.id === value);
                          setEditFormData({
                            ...editFormData,
                            external_consultant_id: value,
                            external_consultant_name: selected?.name || '',
                            external_consultant_email: selected?.email || ''
                          });
                        }
                      }}
                    >
                      <SelectTrigger className="flex-1" data-testid="edit-consultant-select">
                        <SelectValue placeholder="Select consultant (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No consultant</SelectItem>
                        {consultants.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name || c.email} ({c.role})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : null}
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div>
                    <Input
                      value={editFormData.external_consultant_name}
                      onChange={(e) => setEditFormData({ ...editFormData, external_consultant_name: e.target.value })}
                      placeholder="Consultant name"
                      data-testid="edit-consultant-name-input"
                    />
                  </div>
                  <div>
                    <Input
                      type="email"
                      value={editFormData.external_consultant_email}
                      onChange={(e) => setEditFormData({ ...editFormData, external_consultant_email: e.target.value })}
                      placeholder="Consultant email (for login access)"
                      data-testid="edit-consultant-email-input"
                    />
                  </div>
                </div>
                <p className="text-xs text-stone-500 mt-1">
                  Providing an email will auto-create login credentials for the consultant to access this project
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Location</Label>
                  <Input
                    value={editFormData.location}
                    onChange={(e) => setEditFormData({ ...editFormData, location: e.target.value })}
                    placeholder="e.g., Mumbai, Maharashtra"
                    data-testid="edit-location-input"
                  />
                  <p className="text-xs text-stone-500 mt-1">
                    Project site location
                  </p>
                </div>
                <div>
                  <Label>Project Number</Label>
                  <Input
                    value={editFormData.project_number}
                    onChange={(e) => setEditFormData({ ...editFormData, project_number: e.target.value })}
                    placeholder="Unique project identifier"
                    data-testid="edit-project-number-input"
                  />
                  <p className="text-xs text-stone-500 mt-1">
                    Unique project identifier
                  </p>
                </div>
              </div>

              {/* Pricing Model Toggle */}
              <div className="bg-stone-50 rounded-lg p-4 border border-stone-200">
                <Label className="text-sm font-semibold text-stone-800 block mb-3">Pricing Model</Label>
                <div className="flex gap-2">
                  <button type="button"
                    onClick={() => setEditFormData({ ...editFormData, pricing_model: 'vendor_to_client' })}
                    className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium border transition-all ${
                      editFormData.pricing_model !== 'client_to_vendor'
                        ? 'bg-stone-900 text-white border-stone-900'
                        : 'bg-white text-stone-600 border-stone-300 hover:bg-stone-100'
                    }`}
                  >Vendor → Client</button>
                  <button type="button"
                    onClick={() => setEditFormData({ ...editFormData, pricing_model: 'client_to_vendor' })}
                    className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium border transition-all ${
                      editFormData.pricing_model === 'client_to_vendor'
                        ? 'bg-stone-900 text-white border-stone-900'
                        : 'bg-white text-stone-600 border-stone-300 hover:bg-stone-100'
                    }`}
                  >Client → Vendor</button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{editFormData.pricing_model === 'client_to_vendor' ? 'Admin Markdown (%)' : 'Admin Markup (%)'}</Label>
                  <Input
                    type="number"
                    step="0.1"
                    required
                    value={editFormData.admin_markup_percentage}
                    onChange={(e) => setEditFormData({ ...editFormData, admin_markup_percentage: e.target.value })}
                    data-testid="edit-admin-markup-input"
                  />
                  <p className="text-xs text-stone-500 mt-1">
                    {editFormData.pricing_model === 'client_to_vendor' ? 'Markdown on client rate' : 'Markup on vendor rate'}
                  </p>
                </div>
                <div>
                  <Label>{editFormData.pricing_model === 'client_to_vendor' ? 'Vendor Markdown (%)' : 'Client Markup (%)'}</Label>
                  <Input
                    type="number"
                    step="0.1"
                    required
                    value={editFormData.client_markup_percentage}
                    onChange={(e) => setEditFormData({ ...editFormData, client_markup_percentage: e.target.value })}
                    data-testid="edit-client-markup-input"
                  />
                  <p className="text-xs text-stone-500 mt-1">
                    {editFormData.pricing_model === 'client_to_vendor' ? 'Markdown on admin rate' : 'Markup on admin rate'}
                  </p>
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary" data-testid="save-project-button">
                  Save Changes
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Vendor Assignment Dialog - Direct from All Projects */}
        <Dialog open={vendorAssignDialogOpen} onOpenChange={setVendorAssignDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users size={20} className="text-purple-500" />
                Manage Project Vendors
              </DialogTitle>
              <DialogDescription>
                {assigningProject?.name}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 overflow-y-auto flex-1">
              {/* Currently Assigned Vendors */}
              {assignedVendorDetails.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-stone-700 flex items-center gap-2">
                    <Users size={14} className="text-green-600" />
                    Currently Assigned ({assignedVendorDetails.length})
                  </label>
                  <div className="space-y-2">
                    {assignedVendorDetails.map((vendor) => (
                      <div
                        key={vendor.id}
                        className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-green-800">{vendor.name}</p>
                          <p className="text-sm text-green-600">{vendor.email}</p>
                        </div>
                        <button
                          onClick={() => handleUnassignVendor(vendor.id)}
                          disabled={unassigning === vendor.id}
                          className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Remove from project"
                          data-testid={`unassign-vendor-${vendor.id}`}
                        >
                          {unassigning === vendor.id ? (
                            <span className="text-xs">...</span>
                          ) : (
                            <UserMinus size={18} />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Divider */}
              {assignedVendorDetails.length > 0 && (
                <div className="border-t border-stone-200 my-2" />
              )}

              {/* Category Filter */}
              <div className="flex items-center gap-2 p-2 bg-stone-50 rounded-lg">
                <Filter size={14} className="text-stone-400" />
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-full border-0 bg-transparent h-8">
                    <SelectValue placeholder="Filter by category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="electrical">Electrical</SelectItem>
                    <SelectItem value="plumbing">Plumbing</SelectItem>
                    <SelectItem value="carpentry">Carpentry</SelectItem>
                    <SelectItem value="painting">Painting</SelectItem>
                    <SelectItem value="civil">Civil</SelectItem>
                    <SelectItem value="flooring">Flooring</SelectItem>
                    <SelectItem value="fabrication">Fabrication</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Add New Vendors */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-stone-700 flex items-center gap-2">
                  <Plus size={14} className="text-purple-600" />
                  Add Vendors
                </label>
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {vendors.filter(v => !selectedVendors.includes(v.id)).length === 0 ? (
                    <p className="text-center text-stone-500 py-4 text-sm">All vendors are already assigned</p>
                  ) : (
                    vendors.filter(v => !selectedVendors.includes(v.id)).map((vendor) => (
                      <label 
                        key={vendor.id}
                        className="flex items-center gap-3 p-3 rounded-lg border bg-white border-stone-200 hover:bg-stone-50 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={selectedVendors.includes(vendor.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedVendors([...selectedVendors, vendor.id]);
                            } else {
                              setSelectedVendors(selectedVendors.filter(id => id !== vendor.id));
                            }
                          }}
                          className="w-4 h-4 accent-purple-600"
                        />
                        <div className="flex-1">
                          <p className="font-medium">{vendor.name}</p>
                          <p className="text-sm text-stone-500">{vendor.email}</p>
                        </div>
                      </label>
                    ))
                  )}
                </div>
              </div>

              <div className="text-sm text-stone-500 flex items-center justify-between">
                <span>{selectedVendors.length} vendor(s) total</span>
                {selectedVendors.length > assignedVendorDetails.length && (
                  <Badge className="bg-purple-100 text-purple-700">
                    +{selectedVendors.length - assignedVendorDetails.length} new
                  </Badge>
                )}
              </div>
            </div>

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setVendorAssignDialogOpen(false)}>
                Close
              </Button>
              {selectedVendors.length > assignedVendorDetails.length && (
                <Button 
                  onClick={handleAssignVendors} 
                  disabled={assigning}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {assigning ? 'Saving...' : `Add ${selectedVendors.length - assignedVendorDetails.length} Vendor(s)`}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}